import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PLANS } from '../lib/plans';
import useCheckout from '../hooks/useCheckout';

const Billing = () => {
  const { hasAccess } = useAuth();
  const { startCheckout, loading, error } = useCheckout();
  const [selectedPlan, setSelectedPlan] = useState('annual');

  // Already subscribed / in trial — nothing to do here
  if (hasAccess) return <Navigate to="/dashboard" replace />;

  const plan = PLANS.find((p) => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50 flex flex-col items-center justify-center px-4 py-12">
      {/* Logo */}
      <div className="flex items-center gap-3 mb-10">
        <div className="w-10 h-10 stripe-gradient rounded-xl flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 3a2 2 0 00-2 2v1a1 1 0 001 1h14a1 1 0 001-1V5a2 2 0 00-2-2H4zM3 8a1 1 0 011-1h12a1 1 0 011 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8z" />
          </svg>
        </div>
        <span className="text-2xl font-semibold text-gray-900">Journal Coffee</span>
      </div>

      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            7-day free trial
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your first week is on us</h1>
          <p className="text-gray-500">Pick a plan. No charge for 7 days. Cancel before then and you won't pay a thing.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          {/* Plan picker */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {PLANS.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedPlan(p.id)}
                className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                  selectedPlan === p.id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {p.badge && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-bold text-white stripe-gradient px-3 py-0.5 rounded-full whitespace-nowrap">
                    {p.badge}
                  </span>
                )}
                <p className="text-sm font-semibold text-gray-600 mb-1">{p.label}</p>
                <p className="text-2xl font-bold text-gray-900 leading-tight">
                  {p.price}
                  <span className="text-sm font-normal text-gray-500">{p.period}</span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{p.billedAs}</p>
              </button>
            ))}
          </div>

          {/* Features */}
          <ul className="space-y-2.5 mb-6">
            {[
              'Full access to all research papers',
              'Podcast-style audio summaries',
              'PDF downloads',
              'New papers every week',
            ].map((f) => (
              <li key={f} className="flex items-center gap-3 text-sm text-gray-700">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {f}
              </li>
            ))}
          </ul>

          {error && <p className="text-sm text-red-600 mb-3 text-center">{error}</p>}

          <button
            onClick={() => startCheckout(selectedPlan)}
            disabled={loading}
            className="w-full stripe-gradient text-white py-4 rounded-xl font-semibold text-base hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Redirecting to checkout…
              </>
            ) : (
              <>
                Start free trial — then {plan?.price}{plan?.period}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-400 mt-3">
            No charge for 7 days. Cancel before your trial ends and you won't be billed.
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Secured by Stripe · 30-day money-back guarantee after trial
        </p>
      </div>
    </div>
  );
};

export default Billing;
